import { useMachine } from '@xstate/react';
import { useMemo } from 'react';
import { from, Subject } from 'rxjs';
import { filter, map, share } from 'rxjs/operators';
import { createMachine, State } from 'xstate';
import { environment } from '../../config';
import { useSubscription } from '../hooks/useSubscription';
import { BusEvent } from './EditorContext';

export type ComposerEvent =
  | { type: 'STAGE_FOR_SENDING' }
  | { type: 'STAGE_FOR_DELETION' }
  | { type: 'CLEAR' }
  | { type: 'SEND_LETTER' }
  | { type: 'DELETE_LETTER' };

export type ComposerValueState =
  | { value: 'drafting' }
  | { value: 'stagedForSending' }
  | { value: 'sendLetter' }
  | { value: 'stagedForDeletion' }
  | { value: 'deleteLetter' };

export type ComposerState = ComposerValueState & { context: {} };
export type ComposerMachineState = State<any, ComposerEvent, any, ComposerState>;

export const composerEvent = (event: ComposerEvent): BusEvent => ({ type: 'Composer', event });

const fsm = createMachine<{}, ComposerEvent, ComposerState>({
  id: 'Composer',
  initial: 'drafting',
  states: {
    drafting: {
      on: { STAGE_FOR_SENDING: 'stagedForSending', STAGE_FOR_DELETION: 'stagedForDeletion' },
    },
    stagedForSending: { on: { CLEAR: 'drafting', SEND_LETTER: 'sendLetter' } },
    stagedForDeletion: { on: { CLEAR: 'drafting', DELETE_LETTER: 'deleteLetter' } },
    sendLetter: { on: { CLEAR: 'drafting' } },
    deleteLetter: { on: { CLEAR: 'drafting' } },
  },
});

const reportState = ({ value, event }: ComposerMachineState) => {
  environment === 'development' && console.log('Composer', event, '=>', value);
};

export const useComposerState = (bus$: Subject<BusEvent>) => {
  const composer$ = useMemo(
    () =>
      bus$.pipe(
        filter((e) => e.type === 'Composer'),
        map((e) => (e as { type: 'Composer'; event: ComposerEvent }).event)
      ),
    [bus$]
  );

  const [state, send, service] = useMachine<any, ComposerEvent, ComposerState>(fsm);
  const state$ = useMemo(() => from(service).pipe(share()), [service]);
  useSubscription(() => state$.subscribe(reportState), [state$]);
  useSubscription(() => composer$.subscribe(send), [composer$, send]);

  return state as ComposerValueState;
};
